import { useState } from 'react';
import { Input, Label, cn, Checkbox, Textarea } from '@immo/ui-kit';
import {
  AGENT_SKILL_APPROVAL_WINDOW_MAX_DAYS,
  AGENT_SKILL_APPROVAL_WINDOW_MIN_DAYS,
  SKILL_TRIGGER_KINDS,
  type AgentSkillDefinitionStatus,
  type SkillTriggerKind,
} from '@immo/api-contract';
import { useTranslations } from '@/i18n/use-translations';

export const AGENT_SKILLS_PATH = '/admin/skills/agent-skills';

/** The editable shape both the create and the edit page hold in state. */
export type AgentSkillFormState = {
  readonly key: string;
  readonly name: string;
  readonly description: string;
  readonly instructions: string;
  readonly supportedTriggers: ReadonlyArray<SkillTriggerKind>;
  readonly defaultAllowlist: ReadonlyArray<string>;
  readonly approvalWindowDays: number;
};

export const EMPTY_FORM: AgentSkillFormState = {
  key: '',
  name: '',
  description: '',
  instructions: '',
  supportedTriggers: [],
  defaultAllowlist: [],
  approvalWindowDays: AGENT_SKILL_APPROVAL_WINDOW_MAX_DAYS,
};

export function StatusBadge({
  status,
  label,
}: {
  readonly status: AgentSkillDefinitionStatus;
  readonly label: string;
}) {
  return (
    <span
      className={cn(
        'inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium',
        status === 'active' ? 'bg-success-50 text-success-700' : 'bg-subtle text-text-tertiary',
      )}
    >
      {label}
    </span>
  );
}

type AgentSkillFormProps = {
  readonly value: AgentSkillFormState;
  readonly onChange: (next: AgentSkillFormState) => void;
  /** Create shows the key field editable; edit shows it disabled — it is immutable. */
  readonly keyEditable: boolean;
  readonly disabled: boolean;
  readonly error: string | null;
};

function parseAllowlist(raw: string): ReadonlyArray<string> {
  return raw
    .split(',')
    .map((toolId) => toolId.trim())
    .filter((toolId) => toolId.length > 0);
}

export function AgentSkillForm({ value, onChange, keyEditable, disabled, error }: AgentSkillFormProps) {
  const t = useTranslations('pages.admin.skills.agent-skills.editor');
  const tTrigger = useTranslations('pages.admin.skills.agent-skills.trigger');
  // Seeded from the loaded definition and owned by the field thereafter, so the
  // operator's separators and spacing survive their own keystrokes.
  const [allowlistText, setAllowlistText] = useState(() => value.defaultAllowlist.join(', '));

  const patch = (fields: Partial<AgentSkillFormState>) => onChange({ ...value, ...fields });

  const toggleTrigger = (trigger: SkillTriggerKind) => {
    const selected = new Set(value.supportedTriggers);

    if (selected.has(trigger)) selected.delete(trigger);
    else selected.add(trigger);

    patch({ supportedTriggers: SKILL_TRIGGER_KINDS.filter((kind) => selected.has(kind)) });
  };

  return (
    <div className="space-y-4">
      <div className="space-y-1">
        <Label htmlFor="agent-skill-key">{t('key-label')}</Label>
        <Input
          id="agent-skill-key"
          value={value.key}
          disabled={disabled || !keyEditable}
          onChange={(event) => patch({ key: event.target.value })}
        />
        <p className="text-xs text-text-tertiary">{t('key-hint')}</p>
      </div>

      <div className="space-y-1">
        <Label htmlFor="agent-skill-name">{t('name-label')}</Label>
        <Input
          id="agent-skill-name"
          value={value.name}
          disabled={disabled}
          onChange={(event) => patch({ name: event.target.value })}
        />
      </div>

      <div className="space-y-1">
        <Label htmlFor="agent-skill-description">{t('description-label')}</Label>
        <Input
          id="agent-skill-description"
          value={value.description}
          disabled={disabled}
          onChange={(event) => patch({ description: event.target.value })}
        />
        <p className="text-xs text-text-tertiary">{t('description-hint')}</p>
      </div>

      <div className="space-y-1">
        <Label htmlFor="agent-skill-instructions">{t('instructions-label')}</Label>
        <Textarea
          id="agent-skill-instructions"
          value={value.instructions}
          disabled={disabled}
          rows={14}
          onChange={(event) => patch({ instructions: event.target.value })}
          // Instructions are code-like text: mono and denser than a form field.
          className="bg-canvas font-mono text-sm"
        />
        <p className="text-xs text-text-tertiary">{t('instructions-hint')}</p>
      </div>

      <fieldset className="space-y-1">
        <legend className="text-sm font-medium text-text-primary">{t('triggers-label')}</legend>
        <div className="flex flex-wrap gap-3 pt-1">
          {SKILL_TRIGGER_KINDS.map((trigger) => (
            <label key={trigger} className="flex items-center gap-2 text-sm text-text-secondary">
              <Checkbox
                checked={value.supportedTriggers.includes(trigger)}
                disabled={disabled}
                onCheckedChange={() => toggleTrigger(trigger)}
              />
              {tTrigger(trigger)}
            </label>
          ))}
        </div>
        <p className="text-xs text-text-tertiary">{t('triggers-hint')}</p>
      </fieldset>

      <div className="space-y-1">
        <Label htmlFor="agent-skill-allowlist">{t('allowlist-label')}</Label>
        {/* The raw text is held separately from the parsed ids. Deriving the
            field's value from `defaultAllowlist.join(', ')` would re-render the
            separator away the instant it is typed, so a second tool could never
            be entered. */}
        <Input
          id="agent-skill-allowlist"
          value={allowlistText}
          disabled={disabled}
          onChange={(event) => {
            setAllowlistText(event.target.value);
            patch({ defaultAllowlist: parseAllowlist(event.target.value) });
          }}
        />
        <p className="text-xs text-text-tertiary">{t('allowlist-hint')}</p>
      </div>

      <div className="space-y-1">
        <Label htmlFor="agent-skill-approval-window">{t('approval-window-label')}</Label>
        <Input
          id="agent-skill-approval-window"
          type="number"
          min={AGENT_SKILL_APPROVAL_WINDOW_MIN_DAYS}
          max={AGENT_SKILL_APPROVAL_WINDOW_MAX_DAYS}
          value={value.approvalWindowDays}
          disabled={disabled}
          onChange={(event) => patch({ approvalWindowDays: Number(event.target.value) })}
        />
        {/* The range comes from the enforcing constants, never restated in the
            dictionary — a bound stated twice drifts (BL-022). */}
        <p className="text-xs text-text-tertiary">
          {t('approval-window-hint', {
            min: AGENT_SKILL_APPROVAL_WINDOW_MIN_DAYS,
            max: AGENT_SKILL_APPROVAL_WINDOW_MAX_DAYS,
          })}
        </p>
      </div>

      {error !== null && (
        <p role="alert" className="text-sm text-danger-600">
          {error}
        </p>
      )}
    </div>
  );
}
